import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";

export const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/divisions", label: "Our Divisions" },
  { to: "/inventory", label: "Inventory" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/reviews", label: "Client Reviews" },
  { to: "/business-opportunities", label: "Business Opportunities" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 border-b ${
        scrolled || open ? "bg-[#0a0d14]/85 backdrop-blur-xl border-white/10 py-3" : "bg-transparent border-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between gap-6">
        <Link to="/" data-testid="nav-logo-link"><Logo /></Link>
        <nav className="hidden xl:flex items-center gap-5 2xl:gap-7">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              className={({ isActive }) =>
                `relative whitespace-nowrap text-[13px] tracking-wide transition-colors duration-300 ${isActive ? "text-gold" : "text-slate-200 hover:text-gold"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/sourcing" data-testid="nav-sourcing-cta" className="hidden md:inline-flex whitespace-nowrap btn-gold !py-2.5 !px-5">
            Sourcing Requests <ArrowUpRight className="w-4 h-4" />
          </Link>
          <button
            className="xl:hidden w-11 h-11 grid place-items-center border border-white/15 text-slate-100"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="xl:hidden overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-5 pt-6 pb-8 flex flex-col">
              {[...NAV.slice(0, 5), { to: "/sourcing", label: "Asset Sourcing Requests" }, ...NAV.slice(5)].map((n, i) => (
                <motion.div key={n.to} initial={{ x: -16, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: i * 0.04 }}>
                  <Link
                    to={n.to}
                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="block py-3 font-display text-3xl text-slate-100 border-b border-white/10"
                  >
                    {n.label}
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
